import { ErrorBoundary, Head, LogoLoader } from "@/components"
import type { CoordSetAndLookDocument } from "@/lib/prismicio/types.generated"
import { useWishlist } from "@/lib/store/wishlist"
import { useStores } from "@/stores"
import { Container, CoordSetAndLookProvider, Link, NextImage, RichTextHeading } from "@jog/react-components"
import { typedBoolean } from "@jog/shared"
import type { ShopTheLookDetailPage } from "@jog/types"
import { asImageSrc, asText, isFilled } from "@prismicio/helpers"
import { observer } from "mobx-react"

import { Cart } from "./Form"

export const CoordSetAndLook = observer(
    ({
        prismic,
        products,
        last,
    }: {
        prismic: CoordSetAndLookDocument["data"]
        products: ShopTheLookDetailPage<unknown>["products"]
        last?: { text: string; href: string }
    }) => {
        const wishlistLoading = useWishlist.use.actionPending()
        const { cartStore } = useStores()
        return (
            <ErrorBoundary system="coordSetAndLook">
                <Head
                    og={{
                        title: asText(prismic.meta_tag_title),
                        description: asText(prismic.meta_tag_description),
                        image: asImageSrc(prismic.visual),
                    }}
                    title={asText(prismic.meta_tag_title)}
                    robots={asText(prismic.meta_tag_robots)}
                    description={asText(prismic.meta_tag_description)}
                />
                <div className="pb-2.5">
                    {wishlistLoading && <LogoLoader />}
                    {last && (
                        <div className="flex bg-white px-3 py-3 lg:hidden">
                            <Link
                                className="flex items-center text-xs font-medium uppercase leading-4"
                                href={last.href}>
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="7"
                                    height="9"
                                    viewBox="0 0 7 9"
                                    fill="none">
                                    <path
                                        d="M1.37101 4.51445C1.45565 4.55714 1.52229 4.58139 1.57288 4.61691C3.29922 5.82688 5.02507 7.03685 6.74557 8.25057C6.84821 8.32297 6.93626 8.41894 6.98003 8.51524C7.05008 8.67063 6.92847 8.84446 6.73001 8.93427C6.52425 9.02716 6.26012 9.0217 6.06506 8.9172C6.00766 8.88646 5.95707 8.84855 5.90551 8.8127C4.02594 7.49378 2.14686 6.17453 0.267784 4.85528C-0.0907147 4.60359 -0.0892555 4.39731 0.272162 4.14391C2.15951 2.81886 4.04589 1.49277 5.93859 0.170789C6.04463 0.0970225 6.19299 0.0365753 6.33697 0.00993749C6.56268 -0.0320682 6.80297 0.0638961 6.91972 0.20733C7.03695 0.351789 7.01895 0.531765 6.8701 0.662904C6.82486 0.70252 6.77087 0.737695 6.71931 0.773554C5.00318 1.9784 3.28706 3.18325 1.57093 4.38775C1.52375 4.42088 1.47219 4.45059 1.37149 4.51445L1.37101 4.51445Z"
                                        fill="currentColor"
                                    />
                                </svg>
                                <span className="ml-2">{last.text.replace(/-/g, " ").toUpperCase()}</span>
                            </Link>
                        </div>
                    )}
                    <Container className="bg-white">
                        <div className="grid grid-cols-2">
                            <div className="relative col-span-2 h-full lg:col-span-1">
                                <div className="sticky top-0">
                                    <div>
                                        {isFilled.image(prismic.visual) && (
                                            <NextImage
                                                className="w-full"
                                                src={asImageSrc(prismic.visual)}
                                                alt={prismic.visual.alt}
                                                width={prismic.visual.dimensions.width}
                                                height={prismic.visual.dimensions.height}
                                            />
                                        )}
                                    </div>
                                </div>
                            </div>
                            <div className="col-span-2 lg:col-span-1">
                                <div className="px-3 pt-7 pb-4 lg:px-6 lg:pt-0">
                                    {isFilled.richText(prismic.title) && (
                                        <div>
                                            <RichTextHeading field={prismic.title} />
                                        </div>
                                    )}
                                    {isFilled.richText(prismic.description) && (
                                        <div className="mt-5">
                                            <RichTextHeading field={prismic.description} />
                                        </div>
                                    )}
                                    <div className="mt-6 lg:mt-0">
                                        <CoordSetAndLookProvider
                                            addProductsToCart={cartStore.addProductsToCart}
                                            products={products.filter(typedBoolean)}>
                                            <Cart />
                                        </CoordSetAndLookProvider>
                                    </div>
                                    {isFilled.richText(prismic.optional_return_link) && (
                                        <div className="mt-3 lg:mt-5">
                                            <RichTextHeading field={prismic.optional_return_link} />
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </Container>
                </div>
            </ErrorBoundary>
        )
    }
)
